import { FC } from "react";
import {
  Box,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepTitle,
  StepDescription,
  StepSeparator,
  StepNumber,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { borderRadius, borderStyle } from "../../../utils/styles/styles";
import { useMaxStep } from "../../../hooks/stepper/useMaxStep";
import { primaryColor } from "../../../utils/colors";

interface StepperProps {
  steps: { title: string; description: string }[];
  currentStep: number;
  maxWidth?: string;
}

const StepperComponent: FC<StepperProps> = ({
  steps,
  currentStep,
  maxWidth = "60%",
}) => {
  const { maxStepReached } = useMaxStep(currentStep);

  const orientation = useBreakpointValue({
    base: "vertical",
    "2xl": "horizontal",
  }) as "horizontal" | "vertical";

  return (
    <Box
      p={6}
      border={borderStyle}
      borderRadius={borderRadius}
      background="gray.200"
    >
      <Stepper
        index={currentStep}
        size="lg"
        maxWidth={maxWidth}
        orientation={orientation}
      >
        {steps.map((step, index) => (
          <Step key={index}>
            <StepIndicator
              sx={{
                "[data-status=complete] &": {
                  borderWidth: "3px",
                  borderColor: primaryColor,
                  textColor: "white",
                  background:
                    currentStep === index ? primaryColor : "transparent",
                },
                "[data-status=incomplete] &": {
                  borderWidth: "3px",
                  background: "gray.200",
                  borderColor:
                    index > maxStepReached ? "gray.400" : primaryColor,
                  textColor: index > maxStepReached ? "gray.400" : primaryColor,
                },
                "[data-status=active] &": {
                  borderWidth: "3px",
                  background: primaryColor,
                  borderColor: primaryColor,
                  textColor: "white",
                },
              }}
            >
              <StepStatus
                complete={
                  <CheckIcon
                    color={primaryColor}
                    boxSize="100%"
                    height="18px"
                    width="25px"
                  />
                }
                incomplete={<StepNumber />}
                active={<StepNumber />}
              />
            </StepIndicator>
            <Box flexShrink="0">
              <StepTitle>
                <Text fontSize="md" fontWeight="bold">
                  {step.title}
                </Text>
              </StepTitle>
              <StepDescription>
                <Text fontSize="md">{step.description}</Text>
              </StepDescription>
            </Box>
            <StepSeparator
              style={{
                background: index >= maxStepReached ? "#A0AEC0" : "#062952",
              }}
            />
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default StepperComponent;
